import React, { Component } from 'react'
import ScrollMemory from 'react-router-scroll-memory'
import PropTypes from 'prop-types'
import { BrowserRouter, Switch, Route } from 'react-router-dom'
import loadable from '@loadable/component'
import { connect } from 'react-redux'
import { find } from 'lodash/find'
import isEqual from 'lodash/isEqual'
import 'react-dates/initialize'
import { DragDropContextProvider } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import { GoogleOAuthProvider } from '@react-oauth/google'
import ReactGA from 'react-ga4'
import * as Sentry from '@sentry/browser'
import { appPusherSetup } from '../utils/pusher/appPusherSetup'
import { userPresencePusherSetup } from '../utils/pusher/userPresencePusherSetup'
import { authenticate, unauthenticate, logout } from '../actions/session'
import { triggerEnsureClientIdentity } from '../actions/clientIdentity'
import { onVisibilityChange } from '../actions/pageVisibilityState'
import { loadInitialData } from '../actions/initialData'
import TrashContainer from './TrashContainer'
import ModalContainer from './modals/ModalContainer'
import HistoryContainer from './HistoryContainer'
import AccountContainer from './account/AccountContainer'
import QuestionPollDetailContainer from './pollDetail/QuestionPollDetailContainer'
import SetPollDetailContainer from './pollDetail/SetPollDetailContainer'
import SetPollPollDetailContainer from './pollDetail/SetPollPollDetailContainer'
import SetDetailContainer from './itemDetail/SetDetailContainer'
import SetQuestionDetailContainer from './itemDetail/SetQuestionDetailContainer'
import QuestionDetailContainer from './itemDetail/QuestionDetailContainer'
import SearchResultsContainer from './search/SearchResultsContainer'
import FloatingNPControlContainer from './liveView/FloatingNPControlContainer'
import NotificationContainer from './NotificationContainer'
import TopNavContainer from './navigation/TopNavContainer'
import SideNavContainer from './navigation/SideNavContainer'
import LiveViewNotificationContainer from './liveView/LiveViewNotificationContainer'
import LiveViewAutoplayFailedAlertContainer from './liveView/LiveViewAutoplayFailedAlertContainer'
import SyncLearnMoreModalContainer from './SyncLearnMoreModalContainer'
import LiveViewModalContainer from './liveView/LiveViewModalContainer'
import UpgradeContainer from './upgrade/UpgradeContainer'
import ConfirmEmailContainer from './sessions/ConfirmEmailContainer'
import ReleaseNotesContainer from './announcementModals/ReleaseNotesContainer'
import RepoStandardViewContainer from './repo/RepoStandardViewContainer'
import RepoConsumerViewContainer from './repo/RepoConsumerViewContainer'
import PrivateRepoTrashContainer from './repo/PrivateRepoTrashContainer'
import PublishedRepoTrashContainer from './repo/PublishedRepoTrashContainer'
import OnboardingWizardContainer from './onboarding/OnboardingWizardContainer'
import RepoReleaseModalContainer from './RepoReleaseModalContainer'
import AuthorPageContainer from './authorPage/AuthorPageContainer'
import MatchAuthenticated from '../components/routing/MatchAuthenticated'
import RedirectAuthenticated from '../components/routing/RedirectAuthenticated'
import MatchRootAuthenticated from '../components/routing/MatchRootAuthenticated'
import LastLocationProvider from '../components/routing/LastLocationProvider'
import RedirectInvalidUrl from '../components/routing/RedirectInvalidUrl'
import RedirectUnauthenticated from '../components/routing/RedirectUnauthenticated'
import ImportQuestionsHelperContainer from './importQuestions/ImportQuestionsHelperContainer'
import LoginContainer from './sessions/LoginContainer'
import SignupContainer from './sessions/SignupContainer'
import ForgotPasswordContainer from './sessions/ForgotPasswordContainer'
import ResetPasswordContainer from './sessions/ResetPasswordContainer'
import DeleteAccountContainer from './DeleteAccountContainer'
import ScoreSheetContainer from './scoreSheet/ScoreSheetContainer'
import SectionRosterContainer from './sections/SectionRosterContainer'
import MobileUpdateNotificationContainer from './MobileUpdateNotificationContainer'
import SectionContainer from './sections/SectionContainer'
import RecentContainer from './RecentContainer'
import LibraryContainer from './LibraryContainer'
import SlideServiceContainer from './SlideServiceContainer'
import MigrationServiceContainer from './MigrationServiceContainer'
import PackReviewContainer from './explorer/PackReviewContainer'
import DiscoverContainer from './DiscoverContainer'
import ItemPreviewModalContainer from './ItemPreviewModalContainer'
import RichEditorContainer from './richEditor/RichEditorContainer'
import RichStaticSlideTestContainer from './richEditor/RichStaticSlideTestContainer'
import FontSizeCalculationVisualizerPage from '../components/fontSizeCalculationVisualizer/FontSizeCalculationVisualizerPage'
import AutoplaySetupPage from '../components/autoplaySetup/AutoplaySetupPage'
import AutoplaySetupModalContainer from './autoplaySetup/AutoplaySetupModalContainer'
import RichTextMarketingModalContainer from './richTextMarketing/RichTextMarketingModalContainer'
import ExplorerContainer from './explorer/ExplorerContainer'
import ExplorerCreatorsContainer from './explorer/ExplorerCreatorsContainer'
import CreatorsListContainer from './explorer/CreatorsListContainer'
import AddDataOwnerContainer from './AddDataOwnerContainer'
import { checkForExpiredLocks } from '../actions/locks'
// eslint-disable-next-line import/extensions
import 'katex/dist/contrib/mhchem.js' // importing with extension because import resolver catching on .mjs file
import clockSyncService from '../utils/network/clockSyncService'
import PageReloadContainer from './PageReloadContainer'
import { isContentAllowed } from '../utils/cookieHelpers'
import * as configurationProvider from '../utils/configuration/configurationProvider'

const sentryEnvironment = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'unknown'
Sentry.init({
  dsn: `${process.env.REACT_APP_SENTRY_URL}`,
  environment: sentryEnvironment,
  ignoreErrors: [
    'ResizeObserver loop completed with undelivered notifications.', // Ignore harmless error possibly from react-metrics library
    /^"Timeout(?: \([a-zA-Z]\))?"$/, // Ignore timeout errors from unknown source (GA or Google ReCAPTCHA are possible sources)
  ],
})

if (process.env.REACT_APP_GA_MEASUREMENT_ID && process.env.REACT_APP_GA_MEASUREMENT_ID !== 'ga_disabled') {
  ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID)
}

const ScanHelperContainer = loadable(() => import(/* webpackChunkName: 'scanhelper' */ './scanHelper/ScanHelperContainer'))

class AppContainer extends Component {
  constructor() {
    super()
    this.handlePageScroll = this.handlePageScroll.bind(this)
    this.findSidePanel = this.findSidePanel.bind(this)
    this.registerStickyHeader = this.registerStickyHeader.bind(this)
    this.unregisterStickyHeader = this.unregisterStickyHeader.bind(this)
    this.unregisterTopHeader = this.unregisterTopHeader.bind(this)
    this.registerTopHeader = this.registerTopHeader.bind(this)
    this.unregisterSidePanel = this.unregisterSidePanel.bind(this)
    this.registerSidePanel = this.registerSidePanel.bind(this)
    this.pusherSetupSucessful = this.pusherSetupSucessful.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.handleYouTubeScript = this.handleYouTubeScript.bind(this)
    this.stickyHeader = {}
    this.topHeader = {}
    this.sidePanels = []
    this.state = {
      subscribedToPusher: false,
    }
    clockSyncService.loadClockOffset() // also ensures ClockSyncService is started
  }

  async UNSAFE_componentWillMount() {
    const {
      triggerEnsureClientIdentity,
      authenticate,
      unauthenticate,
      loadInitialData,
    } = this.props
    triggerEnsureClientIdentity()
    const session = JSON.parse(localStorage.getItem('ls.session'))
    if (session) {
      const returnedSession = await authenticate()
      if (returnedSession?.user) {
        loadInitialData(returnedSession.user)
      } else {
        unauthenticate()
      }
    } else {
      unauthenticate()
    }
  }

  componentDidMount() {
    configurationProvider.startConfigurationPolling()

    // check if any locks have expired every 10 seconds
    this.interval = setInterval(() => this.props.checkForExpiredLocks(), 10000)

    if (process.env.REACT_APP_ENV !== 'production') {
      console.log('********************')
      console.log(process.env.REACT_APP_ENV)
      console.log('********************')
    }
    window.addEventListener('scroll', this.handlePageScroll)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.handleYouTubeScript()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      !this.state.subscribedToPusher &&
      nextProps.session.sessionInfo &&
      nextProps.session.sessionInfo.user
    ) {
      appPusherSetup(nextProps.session.sessionInfo.user, this.pusherSetupSucessful)
      userPresencePusherSetup(nextProps.session.sessionInfo)
    }
  }

  componentDidUpdate(prevProps) {
    // Check if cookie consents changed
    if (!isEqual(prevProps.cookieConsents, this.props.cookieConsents)) {
      this.handleYouTubeScript()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handlePageScroll)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    clearInterval(this.interval)
    configurationProvider.stopConfigurationPolling()
  }

  pusherSetupSucessful() {
    this.setState({ subscribedToPusher: true })
  }

  handleVisibilityChange() { // Used to know if NP is in visible tab https://www.w3.org/TR/page-visibility/
    const { visibilityState } = document
    this.props.onVisibilityChange(visibilityState)
  }

  handleYouTubeScript() {
    const { cookieConsents } = this.props
    const isYouTubeAllowed = isContentAllowed('youtube', cookieConsents)
    const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]')

    if (isYouTubeAllowed && !existingScript) {
      const tag = document.createElement('script')
      tag.src = `https://www.youtube.com/iframe_api?${Date.now()}`
      document.head.appendChild(tag)
    } else if (!isYouTubeAllowed && existingScript) {
      existingScript.remove()
    }
  }

  // Stuff to register headers/panels throughout app to e.g.performantly make headers sticky
  // use context to make functions available throughout component tree
  getChildContext() {
    return {
      registerStickyHeader: this.registerStickyHeader,
      unregisterStickyHeader: this.unregisterStickyHeader,
      registerTopHeader: this.registerTopHeader,
      unregisterTopHeader: this.unregisterTopHeader,
      registerSidePanel: this.registerSidePanel,
      unregisterSidePanel: this.unregisterSidePanel,
    }
  }

  findSidePanel(node) {
    return find(this.sidePanels, (panel) => panel === node)
  }

  registerStickyHeader(node, height) {
    this.stickyHeader = { node, height }
  }

  registerTopHeader(node, height) {
    this.topHeader = { node, height }
  }

  registerSidePanel(node) {
    if (!this.findSidePanel(node)) {
      this.sidePanels.push(node)
    }
  }

  unregisterStickyHeader() {
    this.stickyHeader = {}
  }

  unregisterTopHeader() {
    this.topHeader = {}
  }

  unregisterSidePanel(node) {
    if (this.findSidePanel(node)) {
      this.sidePanels.splice(this.sidePanels.indexOf(node), 1)
    }
  }

  // attach to scroll event on relevant page elements
  handlePageScroll() {
    const scrollAmount = window.pageYOffset
    const scrollTransitionBuffer = 20
    const showStickyScrollPosition =
      this.topHeader.height - this.stickyHeader.height - scrollTransitionBuffer
    let stickyDisplay
    if (scrollAmount > showStickyScrollPosition) {
      stickyDisplay = 'flex'
    } else {
      stickyDisplay = 'none'
    }
    if (this.stickyHeader.node) {
      this.stickyHeader.node.style.display = stickyDisplay
    }
  }

  render() {
    const { isAuthenticated, willAuthenticate } = this.props
    const authProps = { isAuthenticated, willAuthenticate }
    const loginProps = { isAuthenticated, willAuthenticate }

    return (
      <DragDropContextProvider backend={HTML5Backend}>
        <React.Fragment>
          <BrowserRouter scrollBehaviour='auto'>
            {/* GoogleOAuthProvider used by Sign in with Google.
                this loads Google Identity JavaScript library
                which is also used for Google Classroom Integration */}
            <GoogleOAuthProvider clientId={process.env.REACT_APP_PLICKERS_GOOGLE_AUTH_CLIENT_ID}>
              <LastLocationProvider>

                <div className='App' id='App'>

                  <ScrollMemory />
                  <ModalContainer />
                  <PageReloadContainer />
                  {/* content of reauthenticationModal is created with Portal */}
                  <div id='reauthenticationModal' />
                  <NotificationContainer />
                  <UpgradeContainer />
                  {isAuthenticated &&
                  <SyncLearnMoreModalContainer />}
                  {isAuthenticated &&
                  <AutoplaySetupModalContainer />}
                  {isAuthenticated &&
                  <RichTextMarketingModalContainer />}
                  {isAuthenticated &&
                  <RepoReleaseModalContainer />}
                  {isAuthenticated &&
                  <OnboardingWizardContainer />}
                  {isAuthenticated &&
                  <TopNavContainer />}
                  {isAuthenticated &&
                  <LiveViewNotificationContainer />}
                  {isAuthenticated &&
                  <FloatingNPControlContainer />}
                  {isAuthenticated &&
                  <ReleaseNotesContainer />}
                  {isAuthenticated &&
                  <ItemPreviewModalContainer />}
                  {isAuthenticated &&
                  <LiveViewAutoplayFailedAlertContainer />}
                  {isAuthenticated &&
                  <MobileUpdateNotificationContainer />}
                  {isAuthenticated && (
                  <div className='sideNavContainer sideNavContainer--default'>
                    <SideNavContainer />
                  </div>
                  )}

                  <Switch>
                    <Route path='/slideservice' component={SlideServiceContainer} />
                    <Route path='/migration-service' component={MigrationServiceContainer} />
                    <Route path='/setup-autoplay' component={AutoplaySetupPage} />
                    <Route path='/reset-password' component={ResetPasswordContainer} />
                    <Route path='/delete-account' component={DeleteAccountContainer} />

                    <RedirectAuthenticated path='/login' component={LoginContainer} {...loginProps} />
                    <RedirectAuthenticated path='/signup' component={SignupContainer} {...authProps} />
                    <RedirectAuthenticated path='/forgot-password' component={ForgotPasswordContainer} {...authProps} />
                    <MatchAuthenticated path='/org-connect/:dataOwnerUniqueName' component={AddDataOwnerContainer} {...authProps} />
                    <MatchAuthenticated path='/editor/:id/:output?' component={RichEditorContainer} {...authProps} />
                    <MatchAuthenticated path='/seteditor/:id/:output?' component={RichEditorContainer} {...authProps} />
                    <MatchAuthenticated path='/static/:id' component={RichStaticSlideTestContainer} {...authProps} />
                    <MatchAuthenticated path='/dev/fs' component={FontSizeCalculationVisualizerPage} {...authProps} />
                    <MatchAuthenticated path='/discover/:subject?/:subSubject(biology|chemistry|earthScience|physics)?/:page(latest|popular|rated)?' component={DiscoverContainer} {...authProps} />
                    <MatchAuthenticated path='/explorer/creators' component={ExplorerCreatorsContainer} {...authProps} />
                    <MatchAuthenticated path='/creatorslist' component={CreatorsListContainer} {...authProps} />
                    <MatchAuthenticated path='/explorer/:page?' component={ExplorerContainer} {...authProps} />
                    <MatchAuthenticated path='/packreview' component={PackReviewContainer} {...authProps} />
                    <MatchAuthenticated path='/reports' component={HistoryContainer} {...authProps} />
                    <MatchAuthenticated path='/recent' component={RecentContainer} {...authProps} />
                    <MatchAuthenticated path='/library/:folderId?' title='library' component={LibraryContainer} {...authProps} />
                    <MatchAuthenticated path='/trash' title='trash' component={TrashContainer} {...authProps} />
                    <MatchAuthenticated path='/scoresheet' component={ScoreSheetContainer} {...authProps} />
                    <MatchAuthenticated path='/account' component={AccountContainer} {...authProps} />
                    <MatchAuthenticated path='/setreport/:pollId/:pollPollId' component={SetPollPollDetailContainer} {...authProps} />
                    <MatchAuthenticated path='/setreport/:pollId' component={SetPollDetailContainer} {...authProps} />
                    <MatchAuthenticated path='/questionreport/:pollId' component={QuestionPollDetailContainer} {...authProps} />
                    <MatchAuthenticated path='/classes/:id/:sectionNav' component={SectionContainer} {...authProps} />
                    <MatchAuthenticated path='/classes/:id/' component={SectionContainer} {...authProps} />
                    <MatchAuthenticated path='/classRoster/:id' component={SectionRosterContainer} {...authProps} />
                    <MatchAuthenticated path='/question/:id' component={QuestionDetailContainer} {...authProps} />
                    <MatchAuthenticated path='/set/:id/:questionId' component={SetQuestionDetailContainer} {...authProps} />
                    <MatchAuthenticated path='/set/:id' component={SetDetailContainer} {...authProps} />
                    <MatchAuthenticated path='/packs/:id/trash' component={PrivateRepoTrashContainer} {...authProps} />
                    <MatchAuthenticated path='/packs/:id/:folderId?' component={RepoStandardViewContainer} {...authProps} />
                    <MatchAuthenticated path='/:username/:repoSignature/trash' component={PublishedRepoTrashContainer} {...authProps} />
                    <RedirectUnauthenticated path='/:username/:repoSignature/:folderId?' component={RepoConsumerViewContainer} {...authProps} />
                    <MatchAuthenticated path='/searchresults/' component={SearchResultsContainer} {...authProps} />
                    <MatchAuthenticated path='/confirm-email' component={ConfirmEmailContainer} {...authProps} />
                    <MatchAuthenticated path='/scan-helper' component={ScanHelperContainer} {...authProps} />
                    <MatchAuthenticated path='/import-questions-helper' component={ImportQuestionsHelperContainer} {...authProps} />
                    <MatchAuthenticated path='/claim' component={LibraryContainer} {...authProps} />
                    <MatchAuthenticated path='/:username' component={AuthorPageContainer} {...authProps} />
                    <MatchRootAuthenticated exact path='/' component={RecentContainer} {...authProps} />
                    <RedirectInvalidUrl {...authProps} />
                  </Switch>
                  {isAuthenticated &&
                  <LiveViewModalContainer />}
                </div>
              </LastLocationProvider>
            </GoogleOAuthProvider>
          </BrowserRouter>
        </React.Fragment>
      </DragDropContextProvider>
    )
  }
}

export default connect(
  (state) => ({
    isAuthenticated: state.session.isAuthenticated,
    willAuthenticate: state.session.willAuthenticate,
    currentUser: state.user,
    sections: state.sections,
    session: state.session,
    meta: state.meta,
    control: state.control,
    cookieConsents: state.cookieConsents,
  }),
  {
    authenticate,
    unauthenticate,
    logout,
    triggerEnsureClientIdentity,
    loadInitialData,
    checkForExpiredLocks,
    onVisibilityChange,
  },
)(AppContainer)

AppContainer.childContextTypes = {
  location: PropTypes.object,
  registerStickyHeader: PropTypes.func,
  unregisterStickyHeader: PropTypes.func,
  registerTopHeader: PropTypes.func,
  unregisterTopHeader: PropTypes.func,
  registerSidePanel: PropTypes.func,
  unregisterSidePanel: PropTypes.func,
}
